import { Spinner } from "@blueprintjs/core";
import autobind from "autobind-decorator";
import { generateSchema } from "decorator-form";
import { ISchema } from "decorator-form/dist/generate";
import { Component } from "react";
import Form, { FormProps } from "react-jsonschema-form";

import Class from "core/util/Class";
import IEntity from "core/util/IEntity";
import ServiceRegistry from "core/util/ServiceRegistry";
import { ButtonVariant, ButtonView } from "../../common/views/button/Button.view";
import Label from "./label/Label";
import { iDef } from "core/util/TypeUtils";
import convertToHtml from "common/fragments/ConvertToHtml";

export enum FormMode {
	UPDATE,
	CREATE
}

interface IEntityFormState<T extends IEntity> {
	schema: ISchema;
	data?: T;
	saving: boolean;
	error?: any;
	changes?: number;
}

export interface IEntityFormProps<T extends IEntity> {
	clazz: Class<T>;
	instance?: any; // allow unconstrcted class or instance of it
	refetch?: boolean; // look up the entty again before edting
	onChange?: (data: T) => void;
	afterRefetch?: (data: T) => T; // modify after load, before setting into state
	beforeSave?: (data: T) => void | T | boolean | object; // we can modify the to be saved or cancle the save if desired
	afterSave?: (data: T) => void;
	excludedFields?: Array<keyof T>;
	supplementaryParams?: object;
	config?: any;
	attributeToConvert?: string;
}

export default class EntityForm<T extends IEntity> extends Component<IEntityFormProps<T>, IEntityFormState<T>> {
	private source: Class<T>;
	private mode: FormMode;

	constructor(props) {
		super(props);

		this.mode = typeof props.instance === "object" ? FormMode.UPDATE : FormMode.CREATE;
		this.source = this.props.clazz;
		this.state = { schema: null, saving: false, changes: 0 };
	}

	public async componentDidMount() {
		const { instance, refetch, excludedFields, supplementaryParams = {} } = this.props;

		// generate the form schema from decorators on the source class
		const schema = generateSchema(this.source, undefined, excludedFields as string[]);

		// determing if we need to fetch data based on the type of input entity
		// if its an unconstructed class, then we assume a create mutation.
		// if an object we assume update and get the prototype from the object
		// to generate the query with typegraph

		if (FormMode.UPDATE === this.mode && typeof instance === "object") {

			if (refetch) {
				// assume the query accepts id to fetch a single record
				const params = { ...supplementaryParams, id: instance.id };

				// fetch the data to populate the the update form
				const res: any = await ServiceRegistry.getQueryService().findOne(this.source, params);

				let data = res && res.data && res.data.entity || new this.source();

				// potentially modify the data before rendering
				if (typeof this.props.afterRefetch === 'function') {
					data = this.props.afterRefetch(data);
				}

				// render the form with new data

				this.setState({ schema, data });

			} else {
				// render the form with data directly from props
				this.setState({ schema, data: instance ? instance : new this.source() });
			}
		} else {
			// render a blank form
			this.setState({ schema, data: new this.source() });
		}
	}

	@autobind
	private async onSave() {
		const { beforeSave, afterSave } = this.props;

		let data = this.state.data;
		if (typeof beforeSave === "function") {
			const beforeResult = beforeSave(data) as any;

			if (typeof beforeResult === "object") {
				data = beforeResult;
			}
		}

		this.setState({ saving: true });

		// get the mutation service
		ServiceRegistry.getMutationService().save(data, this.source).then((result) => {

			if (typeof afterSave === "function") {
				afterSave(result.entity);
			}

			this.setState({ saving: false });
		});
	}

	@autobind
	private onChange(form: FormProps<T>) {
		let preData = form.formData;
		const {attributeToConvert, config} = this.props;
		if(iDef(config) && config['htmlEnabled'] && attributeToConvert){
			preData[attributeToConvert] = convertToHtml(preData[attributeToConvert]);
		}
		this.setState({ data: preData, changes: (this.state.changes + 1) });
	}

	public render() {
		const { schema, data, saving, error } = this.state;

		// schema has not been generated yet, don't render the form
		if (!schema) {
			return <Spinner />;
		}

		/*{saving ? (
			<Spinner size={Spinner.SIZE_SMALL} />
		) : (
			<ButtonView viewKey={"entityForm-save"}>
				<Label k="entityForm.save">Save</Label>
			</ButtonView>
		)}*/

		return (
			<div className="entity-form">
				{error && (
					<h1>Error: {JSON.stringify(error)}</h1>
				)}

				<Form
					schema={schema.schema}
					uiSchema={schema.uiSchema}
					fields={schema.fields}
					onChange={this.onChange}
					onSubmit={this.onSave}
					formData={data}
					className={"diform"}
				>
					{saving ? (
						<Spinner size={Spinner.SIZE_SMALL} />
					) : (
							<ButtonView viewKey="entityForm.submit" variant={ButtonVariant.PRIMARY} button={true}>
								<Label k="entityForm.submit" v="Submit" />
							</ButtonView>
						)}
				</Form>
			</div>
		);
	}

}



// WEBPACK FOOTER //
// ./src/core/components/EntityForm.tsx